import { compareDisplayNames } from '@utils/string.util';
import { CommonModule, DatePipe } from '@angular/common';
import { Component, OnInit, Output, ViewChild } from '@angular/core';
import {
  AbstractControl,
  FormsModule,
  FormGroup,
  FormControl,
  ReactiveFormsModule,
  ValidatorFn,
  ValidationErrors,
} from '@angular/forms';
import {
  DeviceAddRequest,
  DeviceContractDto,
  DeviceDeleteRequest,
  ImportDeviceAddRequest,
  ImportDeviceContractDto,
} from '@entities/device';

import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatDialog } from '@angular/material/dialog';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { ja } from 'date-fns/locale';

import { SelectionModel } from '@angular/cdk/collections';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { provideDateFnsAdapter } from '@angular/material-date-fns-adapter';
import {
  MatDatepickerModule,
  MatDateRangePicker,
} from '@angular/material/datepicker';
import {
  DeviceNotaddedListDialogComponent,
  NotAddedDeviceViewModel,
} from '@components/device-list/device-notadded-list-dialog/device-notadded-list-dialog.component';
import { DeviceTypeViewModel } from '@components/device-type-list/device-type-list-table/device-type-list-table.component';
import {
  AlertDialogComponent,
  DialogOption as AlertDialogOption,
} from '@components/shared/alert-dialog/alert-dialog.component';
import { TenantResponse } from '@entities/account';
import { DeviceTypeResponse } from '@entities/deviceType';
import { DeviceService } from '@services/api/device.service';
import { MasterdataService } from '@services/api/masterdata.service';
import { addYears, formatISO, subDays } from 'date-fns';
import { firstValueFrom } from 'rxjs';
import {
  DeviceBulkAddDialogComponent,
  DeviceViewModelBulkAdd,
} from './device-bulk-add-dialog/device-bulk-add-dialog.component';
import {
  DeviceEditDialogComponent,
  DeviceViewModel as DeviceViewModelEdit,
  DialogOption,
} from './device-edit-dialog/device-edit-dialog.component';
import {
  DeviceImportErrorDialogComponent,
  ErrorDialogOption,
} from './device-import-error-dialog/device-import-error-dialog.component';
import {
  DeviceListDeleteDialogComponent,
  DeviceListDeleteDialogOption,
} from './device-list-delete-dialog/device-list-delete-dialog.component';
import {
  DeviceListTableComponent,
  DeviceViewModel,
} from './device-list-table/device-list-table.component';

/** テナント */
export type TenantViewModel = {
  /** テナントID */
  tenantId: string;
  /** テナント名 */
  tenantName: string;
};

/** 登録失敗したデバイスのViewModel */
export type BulkAddFailureViewModel = {
  /** CSVの番号 */
  csvNo: string;
  /** シリアル番号 */
  serialNo: string;
  /** 登録失敗理由 */
  message: string;
};

@Component({
  selector: 'app-device-list',
  standalone: true,
  providers: [
    {
      provide: MAT_DATE_LOCALE,
      useValue: ja,
    },
    provideDateFnsAdapter({
      parse: {
        dateInput: 'yyyy/MM/dd',
      },
      display: {
        dateInput: 'yyyy/MM/dd',
        monthYearLabel: 'yyyy/MM',
        dateA11yLabel: 'yyyy/MM/dd',
        monthYearA11yLabel: 'yyyy/MM',
      },
    }),
  ],
  imports: [
    CommonModule,
    FormsModule,
    MatButtonToggleModule,
    DeviceListTableComponent,
    MatCardModule,
    MatDatepickerModule,
    MatDateRangePicker,
    MatIconModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    DatePipe,
    MatInputModule,
    MatButtonModule,
  ],
  templateUrl: './device-list.component.html',
  styleUrl: './device-list.component.scss',
})
export class DeviceListComponent implements OnInit {
  /** デバイス一覧 */
  public deviceList: DeviceViewModel[] = [];

  /** デバイス種類一覧 */
  public deviceTypeList: DeviceTypeViewModel[] = [];

  /** フィルタ候補のデバイス種類表示名 */
  public deviceTypeDisplayNames: string[] = [];

  /** フィルター用デバイス種類ID一覧 */
  public filterDeviceTypeIdList: string[] = [];

  /** テナント一覧 */
  public tenantList: TenantViewModel[] = [];

  /** 一括登録時のレスポンスのモデル */
  public bulkAddList: DeviceViewModel[] = [];

  /**ディスプレイ表示 */
  public displayFlag: boolean = false;

  @ViewChild(DeviceListTableComponent)
  deviceListTableComponent!: DeviceListTableComponent;

  /** 検索フォーム 検索は日付で行うため、時刻部分を0にする */
  contractFormGroup: FormGroup;

  /** テナントID */
  public tenantId: string = '';

  private dateRangeValidator: ValidatorFn = (
    group: AbstractControl,
  ): ValidationErrors | null => {
    const start = group.get('contractStartedAt')?.value;
    const end = group.get('contractExpiredAt')?.value;
    return start && end && start > end ? { dateRange: true } : null;
  };

  /** 一括登録時にテナントIDを変更しようとして、エラーとなったデバイスIDのリスト */
  @Output() public errorMovedDeviceIds: string[] = [];

  /** 一括登録時に存在しないデバイス種類IDかテナントIDを指定して、エラーとなったデバイスIDのリスト */
  @Output() public errorValidatedDeviceIds: string[] = [];

  /** 一括登録時にシリアル番号を指定しなかったため、エラーとなったデバイスIDのリスト */
  @Output() public errorDevicesWithEmptyIds: string[] = [];

  @Output() public selection = new SelectionModel<DeviceViewModel>(true, []);

  constructor(
    private deviceService: DeviceService,
    private masterdataService: MasterdataService,
    private dialog: MatDialog,
  ) {
    const now = new Date();
    this.contractFormGroup = new FormGroup(
      {
        contractExpiredAt: new FormControl(
          new Date(subDays(addYears(now, 1), 1).setHours(0, 0, 0, 0)),
        ),
        contractStartedAt: new FormControl(new Date(now.setHours(0, 0, 0, 0))),
      },
      { validators: this.dateRangeValidator },
    );
  }

  public ngOnInit(): void {
    // デバイス一覧を取得する
    // this.initDeviceList()

    // マスターデータを取得する
    const dataType = ['tenant', 'device_type'];
    this.masterdataService.find(dataType).subscribe({
      next: (masterdata) => {
        const tenantList = masterdata['tenant'] as TenantResponse[];
        this.tenantList = tenantList.map((tenant) => ({
          tenantId: tenant.tenantId,
          tenantName: tenant.tenantName,
        }));

        const deviceTypeList = masterdata['deviceType'] as DeviceTypeResponse[];
        this.deviceTypeList = deviceTypeList.map((deviceType) => ({
          deviceTypeKey: deviceType.deviceTypeKey,
          deviceTypeId: deviceType.deviceTypeId,
          displayName: deviceType.displayName,
          deviceTypeCount: deviceType.deviceTypeCount,
          description: deviceType.description,
        }));
      },
    });
  }

  /**
   * 検索フォームが全て空である場合に必須入力エラーを付与する
   * @returns
   */
  public isSearchItemsEmpty() {
    const tenantId = this.tenantId;
    const contractStartedAt =
      this.contractFormGroup.get('contractStartedAt')?.value;
    const contractExpiredAt =
      this.contractFormGroup.get('contractExpiredAt')?.value;

    if (!tenantId && !contractStartedAt && !contractExpiredAt) {
      return true;
    } else {
      return false;
    }
  }

  /**
   * 登録されていないデバイスを示すダイアログを表示する
   */
  public async openUnaddedDeviceDialog(): Promise<void> {
    const unaddedDevice: NotAddedDeviceViewModel = await firstValueFrom(
      this.deviceService.compare(),
    );

    const options: NotAddedDeviceViewModel = {
      matp: unaddedDevice.matp,
      qualcomm: unaddedDevice.qualcomm,
    };

    await firstValueFrom(
      this.dialog
        .open(DeviceNotaddedListDialogComponent, {
          width: '700px',
          data: options,
        })
        .afterClosed(),
    );
  }

  /**
   * デバイス追加/編集ダイアログを表示する
   * @param device デバイス
   */
  public async openDeviceEditDialog(device?: DeviceViewModel) {
    const options: DialogOption = {
      updating: false,
      tenantList: this.tenantList,
      deviceTypeList: this.deviceTypeList,
    };

    if (device) {
      options.updating = true;
      options.target = {
        serialNo: device.serialNo,
        deviceTypeId: device.deviceTypeId,
        deviceTypeDisplayName: device.deviceTypeDisplayName,
        tenantId: device.tenantId,
        lpnId: device.lpnId,
        contractStartedAt: device.contractStartedAt,
        contractExpiredAt: device.contractExpiredAt,
      };
    }

    const editedDevice: DeviceViewModelEdit = await firstValueFrom(
      this.dialog
        .open(DeviceEditDialogComponent, {
          width: '700px',
          data: options,
        })
        .afterClosed(),
    );

    if (!editedDevice) {
      return;
    }

    const deviceContract: DeviceContractDto = {
      deviceId: editedDevice.serialNo,
      tenantId: editedDevice.tenantId,
      lpnId: editedDevice.lpnId,
      contractType: null,
      contractStartedAt: editedDevice.contractStartedAt.toISOString(),
      contractExpiredAt: editedDevice.contractExpiredAt.toISOString(),
    };
    const deviceId = editedDevice.serialNo || '';
    const deviceRequest: DeviceAddRequest = {
      deviceId: deviceId,
      deviceTypeId: editedDevice.deviceTypeId,
      deviceTypeDisplayName: editedDevice.deviceTypeDisplayName,
      tenantId: editedDevice.tenantId || '',
      deviceContract: deviceContract,
    };
    if (options.updating) {
      this.deviceService.update(deviceId, deviceRequest).subscribe({
        next: () => {
          // デバイス一覧を再取得する
          this.initDeviceList();
          this.selection.clear();
        },
        error: (error) => {
          let message = '';
          if (
            error.response.status == 400 &&
            error.response.error.detail.type == 'NotFoundWebException'
          ) {
            message =
              '他のユーザーによってデータが変更されたため、更新に失敗しました。最新のデータを取得して再度試してください。';
          } else {
            message =
              'デバイスの変更に失敗しました。\nしばらくしてからもう一度お試しください。';
          }
          const alertDialogOptions: AlertDialogOption = {
            type: 'error',
            message: message,
          };
          this.dialog.open(AlertDialogComponent, {
            width: '600px',
            data: alertDialogOptions,
          });
        },
      });
    } else {
      // デバイスのシリアル番号が重複している場合はエラーダイアログを表示
      const isDuplicateSerialNo =
        this.deviceList.filter(
          (device) => device.serialNo === deviceRequest.deviceId,
        ).length > 0;
      if (isDuplicateSerialNo) {
        const alertDialogOptions: AlertDialogOption = {
          type: 'error',
          message:
            'シリアル番号が重複しているため、追加に失敗しました。\n別のシリアル番号でもう一度お試しください。',
        };
        this.dialog.open(AlertDialogComponent, {
          width: '620px',
          data: alertDialogOptions,
        });
        return;
      }

      // デバイスのシリアル番号が重複していない場合はデバイスを追加
      this.deviceService.add(deviceRequest).subscribe({
        next: () => {
          this.tenantId = deviceRequest.tenantId;
          // デバイス一覧を再取得する
          this.initDeviceList();
          this.selection.clear();
        },
        error: (error) => {
          let message = '';
          if (error.response.error.detail.type == 'AlreadyExistsWebException') {
            message = `シリアル番号が重複しているため、追加に失敗しました。\n別の名前でもう一度お試しください。`;
          } else {
            message = `デバイス追加に失敗しました。\nしばらくしてからもう一度お試しください。`;
          }
          const alertDialogOptions: AlertDialogOption = {
            type: 'error',
            message: message,
          };
          this.dialog.open(AlertDialogComponent, {
            width: '600px',
            data: alertDialogOptions,
          });
        },
      });
    }
  }

  /**
   * デバイスインポートダイアログを表示する
   * @param device デバイス
   */
  public async openDeviceImportDialog() {
    const bulkAddDevices: DeviceViewModelBulkAdd[] | null =
      await firstValueFrom(
        this.dialog
          .open(DeviceBulkAddDialogComponent, {
            width: '600px',
          })
          .afterClosed(),
      );

    if (!bulkAddDevices) {
      return;
    }

    // 契約期間の最大値を取得するための変数の用意。
    let minContractStartedAt: Date | null = null;
    let maxContractExpiredAt: Date | null = null;

    const contractStartedAtMap: Map<string, Date | null> = new Map<
      string,
      Date | null
    >();
    const contractExpiredAtMap: Map<string, Date | null> = new Map<
      string,
      Date | null
    >();

    const deviceRequestList: ImportDeviceAddRequest[] = [];
    for (const bulkAddDevice of bulkAddDevices) {
      // 契約タイプが適切な文字列の場合、対応した値に直す。
      // 適切な文字列でない、または空の場合不正な入力であることを示すinvalid_inputを返す。(バックエンドで不正な入力であるかを判定するのに使用する。)
      const contractType =
        bulkAddDevice.contractType === '有'
          ? '5min'
          : bulkAddDevice.contractType === '無'
            ? null
            : 'invalid_input';

      const parseDate = (date: string | Date) => {
        if (typeof date === 'string') {
          const parsedDate = new Date(date);
          return isNaN(parsedDate.getTime())
            ? 'invalid_date_format'
            : formatISO(parsedDate);
        } else if (date instanceof Date) {
          return formatISO(date);
        }
        return 'invalid_date_format';
      };

      // 契約期間の最大値を取得
      const contractStartedAt = bulkAddDevice.contractStartedAt
        ? new Date(bulkAddDevice.contractStartedAt)
        : null;
      const contractExpiredAt = bulkAddDevice.contractExpiredAt
        ? new Date(bulkAddDevice.contractExpiredAt)
        : null;

      contractStartedAtMap.set(bulkAddDevice.serialNo, contractStartedAt);
      contractExpiredAtMap.set(bulkAddDevice.serialNo, contractExpiredAt);

      const deviceContract: ImportDeviceContractDto = {
        deviceId: bulkAddDevice.serialNo,
        tenantId: bulkAddDevice.tenantId,
        lpnId: bulkAddDevice.lpnId,
        contractType: contractType,
        contractStartedAt: bulkAddDevice.contractStartedAt
          ? parseDate(bulkAddDevice.contractStartedAt)
          : null,
        contractExpiredAt: bulkAddDevice.contractExpiredAt
          ? parseDate(bulkAddDevice.contractExpiredAt)
          : null,
      };

      const deviceId = bulkAddDevice.serialNo || '';
      deviceRequestList.push({
        deviceId: deviceId,
        deviceTypeId: bulkAddDevice.deviceTypeId,
        deviceTypeDisplayName:
          contractType === '5min'
            ? `${bulkAddDevice.deviceTypeId}（5分契約）`
            : bulkAddDevice.deviceTypeId,
        tenantId: bulkAddDevice.tenantId || '',
        deviceContract: deviceContract,
      });
    }

    this.deviceService.bulkAdd(deviceRequestList).subscribe({
      next: (bulkAddList) => {
        const errorTenantDiffDevices: BulkAddFailureViewModel[] =
          Object.entries(bulkAddList.errorTenantDiffDevices).map(
            ([csvNo, device]) => ({
              csvNo,
              serialNo: device.deviceId,
              message: '登録済みデバイスのテナントが一致しません。',
            }),
          );
        const errorNoMasterDevices: BulkAddFailureViewModel[] = Object.entries(
          bulkAddList.errorNoMasterDevices,
        ).map(([csvNo, device]) => ({
          csvNo,
          serialNo: device.deviceId,
          message: '存在しないデバイス種類、またはテナントを指定しています。',
        }));
        const errorInvalidDevices: BulkAddFailureViewModel[] = Object.entries(
          bulkAddList.errorInvalidDevices,
        ).map(([csvNo, device]) => ({
          csvNo,
          serialNo: device.deviceId,
          message: 'データ形式が不正です。',
        }));

        let allErrors = [
          ...errorTenantDiffDevices,
          ...errorNoMasterDevices,
          ...errorInvalidDevices,
        ];
        // csvNoの小さい順にソート
        allErrors = allErrors.sort(
          (a, b) => parseInt(a.csvNo) - parseInt(b.csvNo),
        );

        const errorDeviceIdList = allErrors.map((error) => error.serialNo);
        for (const deviceId of errorDeviceIdList) {
          contractStartedAtMap.delete(deviceId);
          contractExpiredAtMap.delete(deviceId);
        }

        for (const contractStartedAt of contractStartedAtMap) {
          if (contractStartedAt[1]) {
            if (
              !minContractStartedAt ||
              contractStartedAt[1] < minContractStartedAt
            ) {
              minContractStartedAt = contractStartedAt[1];
            }
          }
        }

        for (const contractExpiredAt of contractExpiredAtMap) {
          if (contractExpiredAt[1]) {
            if (
              !maxContractExpiredAt ||
              contractExpiredAt[1] > maxContractExpiredAt
            ) {
              maxContractExpiredAt = contractExpiredAt[1];
            }
          }
        }

        // デバイス一覧を再取得する
        if (minContractStartedAt) {
          this.contractFormGroup.controls['contractStartedAt'].setValue(
            minContractStartedAt,
          );
        }
        if (maxContractExpiredAt) {
          this.contractFormGroup.controls['contractExpiredAt'].setValue(
            maxContractExpiredAt,
          );
        }
        this.initDeviceList();
        this.selection.clear();

        if (allErrors.length > 0) {
          const errorDialogOptions: ErrorDialogOption = {
            type: 'warn',
            message: `以下のデバイスの登録をスキップしました。\n原因を修正のうえ、再度登録を行ってください。`,
            title: 'Warning',
            data: allErrors,
          };
          this.dialog.open(DeviceImportErrorDialogComponent, {
            width: '600px',
            data: errorDialogOptions,
          });
        }
      },
      error: () => {
        const alertDialogOptions: AlertDialogOption = {
          type: 'error',
          message: `デバイス一括登録に失敗しました。\nしばらくしてからもう一度お試しください。`,
        };
        this.dialog.open(AlertDialogComponent, {
          width: '600px',
          data: alertDialogOptions,
        });
      },
    });
  }

  /**
   * デバイスを削除する
   * @param deviceList デバイス一覧
   */
  public async deleteDevice(deviceList: DeviceViewModel[]) {
    const options: DeviceListDeleteDialogOption = {
      title: 'Confirm',
      message: `選択したデバイス ${deviceList.length} 件を削除してよろしいですか？`,
      devices: deviceList.map((device) => ({
        serialNo: device.serialNo,
        tenantName: device.tenantName,
      })),
    };

    const result: boolean = await firstValueFrom(
      this.dialog
        .open(DeviceListDeleteDialogComponent, {
          width: '750px',
          data: options,
        })
        .afterClosed(),
    );

    if (!result) {
      return;
    }

    const today = new Date();
    const deviceRequest: DeviceDeleteRequest[] = [];
    deviceList.forEach((device) => {
      const tmpDevice: DeviceDeleteRequest = {
        tenantId: device.tenantId,
        deviceId: device.serialNo,
        contractStartedAt: device.contractStartedAt,
        contractExpiredAt: device.contractExpiredAt,
      };
      deviceRequest.push(tmpDevice);
    });

    //契約期間内のデバイスが選択されている場合、一括削除全体をキャンセルする
    for (const device of deviceRequest) {
      if (
        device.contractStartedAt !== null &&
        device.contractExpiredAt !== null
      ) {
        const coordinatedContractExpiredAt = new Date(device.contractExpiredAt);
        coordinatedContractExpiredAt.setDate(
          coordinatedContractExpiredAt.getDate() + 1,
        );

        if (
          device.contractStartedAt < today &&
          today < coordinatedContractExpiredAt
        ) {
          const alertDialogOptions: AlertDialogOption = {
            type: 'error',
            message: `デバイス削除に失敗しました。\n契約期間内のデバイスが選択されています。`,
          };
          this.dialog.open(AlertDialogComponent, {
            width: '600px',
            data: alertDialogOptions,
          });
          return;
        }
      }
    }

    this.deviceService.delete(deviceRequest).subscribe({
      next: () => {
        // デバイス一覧を再取得する
        this.initDeviceList();
        this.selection.clear();
      },
      error: () => {
        const alertDialogOptions: AlertDialogOption = {
          type: 'error',
          message: `デバイス削除に失敗しました。\nしばらくしてからもう一度お試しください。`,
        };
        this.dialog.open(AlertDialogComponent, {
          width: '600px',
          data: alertDialogOptions,
        });
      },
    });
  }

  public initDeviceList() {
    // if 引数がなかったらfindAll
    // 引数有ったらfindAll(引数ありのAPIに飛ばす)
    const contractStartedAt =
      this.contractFormGroup.controls['contractStartedAt'].value?.toISOString();
    const contractExpiredAt =
      this.contractFormGroup.controls['contractExpiredAt'].value?.toISOString();
    this.deviceService
      .findAll(this.tenantId, contractStartedAt, contractExpiredAt)
      .subscribe({
        next: (deviceList) => {
          this.deviceList = deviceList.map((device) => ({
            serialNo: device.deviceId,
            deviceName: device.deviceName,
            deviceTypeId: device.deviceTypeId,
            deviceTypeDisplayName: device.deviceTypeDisplayName,
            lastCommunicationAt: device.lastCommunicationAt
              ? new Date(device.lastCommunicationAt)
              : null,
            tenantName: device.tenant?.tenantName || '',
            tenantId: device.tenantId,
            lpnId: device.deviceContract.lpnId,
            contractType: device.deviceContract.contractType,
            contractStartedAt: new Date(
              device.deviceContract.contractStartedAt,
            ),
            contractExpiredAt: new Date(
              device.deviceContract.contractExpiredAt,
            ),
            expiredAt: new Date(device.deviceContract.contractExpiredAt),
          }));

          // 表示名ごとにフィルタ候補を作る
          const displayNames = deviceList.map(
            (device) => device.deviceTypeDisplayName,
          );
          this.deviceTypeDisplayNames = Array.from(new Set(displayNames))
            .filter(Boolean)
            .sort(compareDisplayNames);
        },
      });
    this.deviceListTableComponent.viewChange();
  }
}
